/**
 * Public image URLs always resolve to same-origin /media.
 * Legacy manager, /uploads, and /sctmanager paths are rewritten onto that mount.
 */
import { getRuntimeEnv, isRuntimeDev, isRuntimeProd } from "./runtimeEnv";

// Get environment-based URLs
const isDev = isRuntimeDev();
const isProd = isRuntimeProd();

/**
 * Normalize mixed image payloads to a plain src string.
 * Handles raw strings, static import objects ({src}), and API media objects.
 */
export function toImageSrc(value, fallback = null) {
  if (value === null || value === undefined) return fallback;
  if (Array.isArray(value)) {
    return toImageSrc(value[0], fallback);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === 'object') {
    const candidate =
      value.src ||
      value.url ||
      value.file_url ||
      value.image_url ||
      value.image ||
      value.path ||
      value.default ||
      value.thumbnails?.medium?.url ||
      value.thumbnails?.large?.url ||
      value.thumbnails?.small?.url ||
      null;
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      return trimmed || fallback;
    }
  }
  return fallback;
}

/**
 * Normalize route/path segment values so object payloads never become "[object Object]".
 */
export function toPathSegment(value, fallback = '') {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    const v = value.trim();
    return v || fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return toPathSegment(value[0], fallback);
  }
  if (typeof value === 'object') {
    const candidate =
      value.slug ??
      value.id ??
      value.code ??
      value.value ??
      value.name ??
      null;
    return toPathSegment(candidate, fallback);
  }
  return fallback;
}


/**
 * Get the current domain from window.location (runtime detection)
 * This works in production when build-time env vars aren't available
 */
function getCurrentDomain() {
  if (typeof window !== 'undefined' && window.location) {
    const origin = window.location.origin;
    if (origin) {
      return origin;
    }
  }
  return '';
}

function isManagerAssetHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host) return false;
  return (
    host === "manager.southcrete.com"
    || host === "manager.southcrete.gr"
    || host.startsWith("manager.")
    || host.startsWith("dev.manager.")
    || host.startsWith("staging.manager.")
    || host.includes("tk4uev6ka7fgm")
  );
}

function isPublicMediaPath(pathname) {
  return /\/(pictures|videos|pdf|uploads|media|sctmanager)\//i.test(String(pathname || ""));
}

/**
 * Ensure URL uses HTTPS in production (critical for mobile browsers)
 * Mobile browsers (especially iOS Safari) are strict about mixed content
 * This must be defined before getMediaBaseUrl() since it's used there
 */
function ensureHttps(url) {
  if (!url || typeof url !== 'string') return url;
  
  // Only enforce HTTPS in production
  if (!isProd) return url;
  
  // Replace http:// with https:// for production URLs
  // This prevents mixed content issues on mobile browsers
  if (url.startsWith('http://') && !url.includes('localhost')) {
    return url.replace('http://', 'https://');
  }
  
  return url;
}

/**
 * Get media base URL (host/root for media library paths like /pictures/...)
 */
function getMediaBaseUrl() {
  // Keep this isomorphic: absolute window.origin URLs on the client and empty
  // strings on the server caused hydration mismatches (fallback jpg vs /media).
  const rawMediaBase = getRuntimeEnv("VITE_MEDIA_BASE_URL", "");
  if (rawMediaBase !== undefined && rawMediaBase !== null) {
    const v = String(rawMediaBase).trim();
    const lowered = v.toLowerCase();
    if (lowered && lowered !== 'null' && lowered !== 'undefined') {
      if (v.startsWith('/')) {
        return v.replace(/\/+$/, '') || '/media';
      }
      if (/^https?:\/\//i.test(v)) {
        try {
          const envUrl = new URL(v);
          if (
            envUrl.pathname === '/media' ||
            envUrl.pathname === '/media/' ||
            isManagerAssetHost(envUrl.hostname)
          ) {
            return '/media';
          }
          return ensureHttps(v.replace(/\/+$/, ''));
        } catch {
          return '/media';
        }
      }
    }
  }
  return '/media';
}

function extractPublicMediaPath(pathname) {
  let path = String(pathname || "");
  try {
    if (/^https?:\/\//i.test(path)) {
      path = new URL(path).pathname || "";
    }
  } catch {
    // keep original path
  }
  path = path.replace(/^\/+/, "");
  path = path.replace(/^(?:southcrete\/)?(?:site-next\/|site\/)?media\/+/i, "");

  const mediaMatch = path.match(/(?:^|\/)((?:pictures|videos|pdf)\/.*)$/i);
  if (mediaMatch) return mediaMatch[1].replace(/^\/+/, "");

  path = path.replace(/^(?:.*\/)?(?:sctmanager\/|southcrete\/manager\/|backend\/)?uploads\/+/i, "");
  path = path.replace(/^sctmanager\/+/i, "");

  if (/^(destinations|hotels|excursions|regions|car-rental|transfers|about|home|services|east-crete|book)\//i.test(path)) {
    return `pictures/${path}`;
  }
  return path;
}

function joinPublicMediaUrl(relativePath) {
  const mediaBase = getMediaBaseUrl() || "/media";
  const rel = String(relativePath || "").replace(/^\/+/, "");
  if (!rel) return String(mediaBase).replace(/\/+$/, "");
  return `${String(mediaBase).replace(/\/+$/, "")}/${rel}`;
}

export function rewritePublicAssetUrl(url) {
  const raw = toImageSrc(url, null);
  if (!raw) return raw;

  const value = raw.startsWith("//") ? `https:${raw}` : raw;

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      const path = parsed.pathname || "";
      const shouldRewrite =
        (isManagerAssetHost(parsed.hostname) && isPublicMediaPath(path))
        || /\/uploads\//i.test(path)
        || /\/sctmanager\//i.test(path)
        || /\/backend\/uploads\//i.test(path);
      if (shouldRewrite) {
        return joinPublicMediaUrl(extractPublicMediaPath(path));
      }
      return ensureHttps(value);
    } catch {
      return value;
    }
  }

  if (
    /\/uploads\//i.test(value)
    || /\/sctmanager\//i.test(value)
    || /manager\.southcrete/i.test(value)
  ) {
    return joinPublicMediaUrl(extractPublicMediaPath(value));
  }

  return value;
}

function getOrigin(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    return new URL(url).origin;
  } catch {
    return url;
  }
}

let mediaConnectionsWarmed = false;

export function warmMediaConnections() {
  if (mediaConnectionsWarmed) return;
  if (typeof document === 'undefined') return;
  mediaConnectionsWarmed = true;

  const origins = new Set();
  const mediaBase = getMediaBaseUrl();

  function addOrigin(url) {
    if (!url) return;
    const origin = getOrigin(url);
    if (!origin) return;
    try {
      const u = new URL(origin);
      if (!u.hostname || u.hostname === 'uploads' || isManagerAssetHost(u.hostname)) return;
    } catch { return; }
    origins.add(origin);
  }
  addOrigin(mediaBase);

  origins.forEach((origin) => {
    if (!origin) return;
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = origin;
    preconnect.crossOrigin = '';
    document.head.appendChild(preconnect);

    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = origin;
    document.head.appendChild(dnsPrefetch);
  });
}

/**
 * Rewrite leftover localhost / old site-media hosts onto the public /media mount.
 */
export function replaceLocalhostUrl(url) {
  if (!url || typeof url !== 'string') return url;

  const mediaBase = getMediaBaseUrl();
  let replacedUrl = url;

  if (mediaBase) {
    replacedUrl = replacedUrl
      .replace(/https?:\/\/[^/]+\/southcrete\/site-next\/media/g, mediaBase)
      .replace(/https?:\/\/[^/]+\/southcrete\/site\/media/g, mediaBase)
      .replace(/https?:\/\/[^/]+\/site-next\/media/g, mediaBase)
      .replace(/https?:\/\/[^/]+\/site\/media/g, mediaBase);
  }

  const finalUrl = rewritePublicAssetUrl(isProd ? ensureHttps(replacedUrl) : replacedUrl);
  if (isProd && (String(finalUrl).includes('localhost') || String(finalUrl).includes('127.0.0.1'))) {
    return '';
  }
  return finalUrl;
}

/**
 * Normalize media URLs from the manager or DB.
 * Handles full URLs, localhost URLs, and relative media paths.
 */
/** Strip legacy media path prefixes so path is e.g. pictures/regions/x.jpg */
function stripMediaPrefix(path) {
  if (!path || typeof path !== 'string') return path;
  let p = path.startsWith('/') ? path.slice(1) : path;
  const prefixes = ['site-next/media/', 'site/media/', 'southcrete/site-next/media/', 'southcrete/site/media/', 'southcrete-media/'];
  for (const pre of prefixes) {
    if (p.startsWith(pre)) {
      p = p.slice(pre.length);
      break;
    }
  }
  return p;
}

export function getMediaUrl(path, fallback = null) {
  const normalizedInput = toImageSrc(path, null);
  if (!normalizedInput) return fallback;

  if (normalizedInput.startsWith('http://') || normalizedInput.startsWith('https://') || normalizedInput.startsWith('//')) {
    return replaceLocalhostUrl(normalizedInput);
  }

  const rewrittenRelative = rewritePublicAssetUrl(normalizedInput);
  if (rewrittenRelative && rewrittenRelative !== normalizedInput && /^(https?:)?\/\//.test(rewrittenRelative)) {
    return rewrittenRelative;
  }

  const mediaBaseUrl = getMediaBaseUrl();
  if (!mediaBaseUrl) {
    return fallback;
  }

  const cleanPath = stripMediaPrefix(normalizedInput);
  const normalizedBase = String(mediaBaseUrl).replace(/\/+$/, '');
  const normalizedPath = String(cleanPath).replace(/^\/+/, '');
  return `${normalizedBase}/${normalizedPath}`;
}

/**
 * Get full image URL from filename
 * @param {string} filename - Image filename from database (just filename, no path)
 * @param {string} type - Image type: 'destinations', 'excursions/highlights', etc.
 * @param {string} fallback - Fallback image URL if filename is empty
 * @returns {string} Full image URL
 */
export function getImageUrl(filename, type = 'destinations', fallback = null) {
  const safeFallback = toImageSrc(fallback, null);
  const normalized = toImageSrc(filename, null);
  if (!normalized) return safeFallback;

  if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('//')) {
    return replaceLocalhostUrl(normalized);
  }

  const cleanFilename = normalized.replace(/^.*\//, '').trim();
  if (!cleanFilename) return safeFallback;

  const folder = type === 'excursions/highlights' ? 'excursions' : String(type || 'destinations').replace(/^\/+|\/+$/g, '');
  return joinPublicMediaUrl(`pictures/${folder}/${cleanFilename}`);
}

/**
 * Get destination image URL
 * @param {object|string} destination - Destination object with image property, or filename string
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getDestinationImage = (destination, fallback = null) => {
  const safeFallback = toImageSrc(fallback, null);
  const filename = typeof destination === 'string'
    ? destination
    : (destination?.image_url || destination?.image || destination?.file_url);
  const normalized = toImageSrc(filename, null);
  if (!normalized) return safeFallback;

  if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('//')) {
    return replaceLocalhostUrl(normalized);
  }

  const path = stripMediaPrefix(normalized);
  // Public media only. Bare destinations.image names (always-at-ease-we-have.jpg,
  // dest_….jpeg) are leftover manager/uploads filenames and are not on /media.
  if (path.includes('pictures/') || path.startsWith('media/') || path.startsWith('/media/')) {
    return getMediaUrl(path, safeFallback);
  }
  return safeFallback;
};

/**
 * Get region image URL
 * @param {object|string} region - Region object with image property, or filename string
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getRegionImage = (region, fallback = null) => {
  // API returns 'image_url' field (from media_relationships/media_library)
  // Check both image_url and image for backwards compatibility
  const imagePathRaw = typeof region === 'string'
    ? region
    : (region?.image_url || region?.image || region?.file_url || region?.url);
  const imagePath = toImageSrc(imagePathRaw, null);
  
  if (!imagePath) {
    return fallback;
  }
  
  // If imagePath already contains the full URL, check if it needs localhost replacement
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return replaceLocalhostUrl(imagePath);
  }
  
  const mediaBaseUrl = getMediaBaseUrl();
  if (!mediaBaseUrl) {
    return fallback;
  }
  const path = stripMediaPrefix(imagePath);
  // If path starts with /pictures/regions/, it's already a relative path from media base
  if (path.startsWith('pictures/regions/') || path.startsWith('/pictures/regions/')) {
    const p = path.startsWith('/') ? path : '/' + path;
    return `${mediaBaseUrl}${p}`;
  }
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${mediaBaseUrl}/pictures/regions/${cleanPath}`;
};

/**
 * Get excursion highlight image URL
 * @param {object|string} image - Image object with image_path, or path string
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getHighlightImage = (image, fallback = null) => {
  let filename;
  
  if (typeof image === 'string') {
    filename = image;
  } else if (image?.image_path) {
    filename = image.image_path;
  } else {
    return fallback;
  }
  
  // If already a full URL, check if it needs localhost replacement
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return replaceLocalhostUrl(filename);
  }

  // Clean path - remove leading slashes and extract filename
  const cleanFilename = filename.replace(/^.*\//, '').trim();
  
  if (!cleanFilename) return fallback;
  
  return getImageUrl(cleanFilename, 'excursions/highlights', fallback);
};

/**
 * Get hotel image URL
 * Handles paths that may include:
 * - /pictures/hotels/ and legacy /uploads/ paths -> public /media
 * Works for both local development and Upsun production
 * @param {string|array} imagePath - Image path string or array of image paths
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getHotelImage = (imagePath, fallback = null) => {
  // Ensure we always return a string or null, never undefined
  const safeFallback = fallback !== undefined ? fallback : null;
  
  if (!imagePath) {
    return safeFallback;
  }
  
  let path = toImageSrc(imagePath, null);
  if (!path) {
    return safeFallback;
  }

  // API payloads can include media objects instead of plain URL strings.
  if (typeof path === 'object') {
    const candidate =
      path.file_url ||
      path.url ||
      path.image_url ||
      path.image ||
      path.path ||
      path.src ||
      path.thumbnails?.medium?.url ||
      path.thumbnails?.large?.url ||
      path.thumbnails?.small?.url ||
      null;
    if (!candidate || typeof candidate !== 'string') {
      return safeFallback;
    }
    path = candidate;
  }
  
  // Convert to string if it's not already
  path = String(path).trim();
  
  if (!path || path === '') {
    return safeFallback;
  }
  
  // If already a full URL, check if it needs localhost replacement
  if (path.startsWith('http://') || path.startsWith('https://')) {
    const result = replaceLocalhostUrl(path);
    return result || safeFallback;
  }
  path = stripMediaPrefix(path);
  // Check if this is a media file path: "pictures/hotels/" or "/pictures/hotels/" (stripMediaPrefix can remove leading slash)
  const hasPicturesHotels = path.includes('pictures/hotels/') || path.startsWith('/pictures/hotels/');
  if (hasPicturesHotels) {
    const mediaBaseUrl = getMediaBaseUrl();
    if (!mediaBaseUrl) {
      return fallback;
    }
    // Ensure path has leading slash: mediaBaseUrl + /pictures/hotels/hotel_123/xyz.jpg
    const slashPath = path.startsWith('/') ? path : `/${path}`;
    return `${mediaBaseUrl}${slashPath}`;
  }
  
  // Handle uploaded hotel images (/backend/uploads/ or /uploads/)
  let cleanPath = String(path).trim();
  
  // Remove /backend/uploads/ prefix if present
  if (cleanPath.includes('/backend/uploads/')) {
    cleanPath = cleanPath.replace(/^.*\/backend\/uploads\//, '');
  }
  // Remove /uploads/ prefix if present
  else if (cleanPath.includes('/uploads/')) {
    cleanPath = cleanPath.replace(/^.*\/uploads\//, '');
  }
  // Remove leading slashes
  else if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.substring(1);
  }
  
  // Extract just the filename (in case path includes subdirectories)
  const filename = cleanPath.replace(/^.*\//, '').trim();
  
  if (!filename || filename === '') {
    return safeFallback;
  }
  
  // Use getImageUrl to construct the full URL for hotels
  const result = getImageUrl(filename, 'hotels', safeFallback);
  
  // Ensure we never return undefined
  if (result === undefined) {
    return safeFallback;
  }
  
  return result || safeFallback;
};

/**
 * Get excursion image from highlight images array
 * Handles paths that may include:
 * - /pictures/excursions/ and leftover /uploads/highlights paths -> public /media
 * Works for both local development and Upsun production
 * @param {string|array} imagePath - Image path string or array of image paths
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getExcursionImage = (imagePath, fallback = null) => {
  // Ensure we always return a string or null, never an import object.
  const safeFallback = toImageSrc(fallback, null);
  
  if (!imagePath) {
    return safeFallback;
  }
  
  // Normalize mixed payloads first so objects never become "[object Object]".
  const normalizedPath = toImageSrc(imagePath, null);
  if (!normalizedPath) {
    return safeFallback;
  }
  
  // Convert to string if it's not already
  let path = String(normalizedPath).trim();
  
  if (!path || path === '') {
    return safeFallback;
  }
  
  // If already a full URL, check if it needs localhost replacement
  if (path.startsWith('http://') || path.startsWith('https://')) {
    const result = replaceLocalhostUrl(path);
    return result || safeFallback;
  }
  path = stripMediaPrefix(path);
  const mediaBaseUrl = getMediaBaseUrl();

  // Handle media-root paths first (e.g. /media/pictures/home/x.jpg).
  if (path.startsWith('/media/') || path.startsWith('media/')) {
    if (!mediaBaseUrl) {
      return safeFallback;
    }
    const relativeMediaPath = path.replace(/^\/?media\/+/, '');
    return `${mediaBaseUrl.replace(/\/$/, '')}/${relativeMediaPath}`;
  }

  // Handle any pictures/* path from media library (not only pictures/excursions).
  const picturesIdx = path.indexOf('pictures/');
  if (picturesIdx !== -1) {
    if (!mediaBaseUrl) {
      return safeFallback;
    }
    const relativePicturesPath = path.slice(picturesIdx).replace(/^\/+/, '');
    return `${mediaBaseUrl.replace(/\/$/, '')}/${relativePicturesPath}`;
  }

  // Bare filename from media library (common in legacy excursion payloads).
  // Prefer the public media mount over /uploads/highlights so migrated data keeps working.
  if (!path.includes('/') && mediaBaseUrl) {
    const cleanFile = path.replace(/^\/+/, '');
    return `${mediaBaseUrl.replace(/\/$/, '')}/pictures/excursions/${cleanFile}`;
  }

  // Legacy shorthand: bare excursion filename should still map to excursions folder.
  if (path.startsWith('excursion_') || path.startsWith('/excursion_')) {
    if (!mediaBaseUrl) {
      return safeFallback;
    }
    const normalized = String(path).replace(/^\/+/, '');
    return `${mediaBaseUrl.replace(/\/$/, '')}/pictures/excursions/${normalized}`;
  }
  
  // Handle uploaded highlight images (/backend/uploads/ or /uploads/)
  let cleanPath = String(path).trim();
  
  // Remove /backend/uploads/ prefix if present
  if (cleanPath.includes('/backend/uploads/')) {
    cleanPath = cleanPath.replace(/^.*\/backend\/uploads\//, '');
  }
  // Remove /uploads/ prefix if present
  else if (cleanPath.includes('/uploads/')) {
    cleanPath = cleanPath.replace(/^.*\/uploads\//, '');
  }
  // Remove leading slashes
  else if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.substring(1);
  }
  
  // Extract just the filename (in case path includes subdirectories)
  const filename = cleanPath.replace(/^.*\//, '').trim();
  
  if (!filename || filename === '') {
    return safeFallback;
  }
  
  // Use getImageUrl to construct the full URL for highlights
  const result = getImageUrl(filename, 'excursions/highlights', safeFallback);
  
  // Ensure we never return undefined
  if (result === undefined) {
    return safeFallback;
  }
  
  return result || safeFallback;
};

/**
 * Normalize image path helper (for backwards compatibility)
 * @deprecated Use getDestinationImage or getHighlightImage instead
 */
export const normalizeImagePath = (imagePath) => {
  if (!imagePath) return null;
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return replaceLocalhostUrl(imagePath);
  }

  // Extract just the filename
  const cleanFilename = imagePath.replace(/^.*\//, '').trim();
  
  if (!cleanFilename) return null;
  
  // Return path ready for getImageUrl
  return cleanFilename;
};

/**
 * Get about page image URL
 * Handles paths that may include:
 * - /pictures/about/ (media files) -> use MEDIA_BASE_URL
 * - Just filenames -> assume about, use MEDIA_BASE_URL
 * Works for both local development and Upsun production
 * @param {string} imagePath - Image path string
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getAboutImage = (imagePath, fallback = null) => {
  const safeFallback = toImageSrc(fallback, null);
  const normalized = toImageSrc(imagePath, null);
  if (!normalized) return safeFallback;
  const path = String(normalized).trim();
  if (!path || path === '[object Object]') return safeFallback;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return replaceLocalhostUrl(path) || safeFallback;
  }
  const result = path.includes('pictures/') || path.startsWith('/')
    ? getMediaUrl(path)
    : getMediaUrl(`pictures/about/${path}`);
  return result || safeFallback;
};

/**
 * Get Book page image URL
 * Handles paths that may include:
 * - /pictures/book/ (media files) -> use MEDIA_BASE_URL
 * - Full URLs -> use as-is (with localhost replacement if needed)
 * @param {string} imagePath - Image path string
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getBookImage = (imagePath, fallback = null) => {
  const safeFallback = toImageSrc(fallback, null);
  const normalized = toImageSrc(imagePath, null);
  if (!normalized) return safeFallback;
  const path = String(normalized).trim();
  if (!path) return safeFallback;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return replaceLocalhostUrl(path) || safeFallback;
  }
  const result = path.includes('pictures/') || path.startsWith('/')
    ? getMediaUrl(path)
    : getMediaUrl(`pictures/book/${path}`);
  return result || safeFallback;
};

/**
 * Get East Crete page image URL
 * Handles paths that may include:
 * - /pictures/east-crete/ (media files) -> use MEDIA_BASE_URL
 * - Full URLs -> use as-is (with localhost replacement if needed)
 * @param {string} imagePath - Image path string
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getEastCreteImage = (imagePath, fallback = null) => {
  const safeFallback = fallback !== undefined ? fallback : null;
  const normalized = toImageSrc(imagePath, null);
  if (!normalized) return safeFallback;
  const path = String(normalized).trim();
  if (!path) return safeFallback;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return replaceLocalhostUrl(path) || safeFallback;
  }
  const result = path.includes('pictures/') || path.startsWith('/')
    ? getMediaUrl(path)
    : getMediaUrl(`pictures/east-crete/carousel/${path}`);
  return result || safeFallback;
};

/**
 * Get Home page image URL
 * Handles paths that may include:
 * - /pictures/home/ (media files) -> use MEDIA_BASE_URL
 * - /pictures/ (any media path) -> use getMediaUrl
 * - Full URLs -> use as-is (with localhost replacement if needed)
 * @param {string} imagePath - Image path string
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full image URL
 */
export const getHomeImage = (imagePath, fallback = null) => {
  const safeFallback = fallback !== undefined ? fallback : null;

  const normalized = toImageSrc(imagePath, null);
  if (!normalized) {
    return safeFallback;
  }

  const path = String(normalized).trim();

  if (!path || path === '') {
    return safeFallback;
  }

  if (path.startsWith('http://') || path.startsWith('https://')) {
    const result = replaceLocalhostUrl(path);
    return result || safeFallback;
  }

  // Use getMediaUrl for any path that looks like media (pictures/, /pictures/, etc.)
  if (path.includes('pictures/') || path.startsWith('/')) {
    const result = getMediaUrl(path);
    return result || safeFallback;
  }
  // Bare filename -> assume pictures/home/
  const result = getMediaUrl(`pictures/home/${path}`);
  return result || safeFallback;
};

export default getImageUrl;

